import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { usePharmacy } from '@/context/PharmacyContext';

export default function AdminLoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { signIn, user, isAdmin } = useAuth();
  const { settings } = usePharmacy();
  const navigate = useNavigate();

  if (user && isAdmin) {
    navigate('/admin/dashboard', { replace: true });
    return null;
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email || !password) {
      setError('Please enter both email and password.');
      return;
    }
    setSubmitting(true);
    const result = await signIn(email, password);
    setSubmitting(false);
    if (!result.success) {
      setError(result.error || 'Invalid credentials. Please try again.');
    } else {
      navigate('/admin/dashboard', { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-background-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <i className="ri-shield-check-line text-white text-2xl"></i>
          </div>
          <h1 className="text-2xl font-bold text-foreground-950">{settings?.pharmacy_name || 'Admin'} Portal</h1>
          <p className="text-sm text-foreground-600 mt-1">Sign in to manage your pharmacy site</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-background-50 border border-background-200 rounded-xl p-6 sm:p-8 space-y-5">
          <div>
            <label htmlFor="email" className="block text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2">
              Email Address
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 focus:ring-2 focus:ring-primary-400 focus:border-transparent text-sm outline-none transition"
              placeholder="admin@example.com"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 focus:ring-2 focus:ring-primary-400 focus:border-transparent text-sm outline-none transition"
              placeholder="••••••••"
            />
          </div>

          {error && (
            <div className="p-3 bg-accent-100 border border-accent-200 rounded-lg flex items-start gap-2.5">
              <i className="ri-error-warning-line text-accent-600 text-base mt-0.5 flex-shrink-0"></i>
              <p className="text-sm text-accent-800">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 font-semibold px-6 py-3 rounded-lg text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
          >
            {submitting ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                Signing in...
              </span>
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        <p className="text-center text-xs text-foreground-500 mt-6">
          <i className="ri-lock-line mr-1"></i>
          Secure admin access only
        </p>
      </div>
    </div>
  );
}